import { FC, useEffect, useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { isWeaveUser } from '@frontend/auth-helpers';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { FeatureSubscriptionGuard } from '@frontend/self-upgrade';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { Outlet, useNavigate } from '@frontend/weave-router';
import { usePracticeAnalyticsShallowStore } from '../components/practice/hooks/use-practice-analytics-store';
import { URLs } from '../constants';

export const PracticeAnalyticsPage: FC = () => {
  const navigate = useNavigate();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showPADemoModal = getFeatureFlagValue('practice-analytics-demo-inside-modal');

  const { selectedLocationIds } = useAppScopeStore();

  const { isLoading: isCheckingPromoteState, locationIdWiseCustomizationFlagDetails } =
    CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
      locationIds: selectedLocationIds,
      customizationFlag: Feature.PRACTICE_ANALYTICS,
      activeStates: [CustomizationFlagTypes.CustomizationFlagState.PROMOTE],
    });

  const { isDemoModal, setIsDemoModal } = usePracticeAnalyticsShallowStore('isDemoModal', 'setIsDemoModal');
  const { setMode } = useSlidePanelShallowStore('setMode');

  const skipTeaserForWeaveUser = useMemo(() => {
    if (isCheckingPromoteState) {
      return false;
    }
    return isWeaveUser() && Object.values(locationIdWiseCustomizationFlagDetails).some((isPromote) => !!isPromote);
  }, [isCheckingPromoteState, locationIdWiseCustomizationFlagDetails]);

  const handleDemoModalClose = () => {
    setIsDemoModal(false);
    navigate({ to: '/analytics/practice/main' });
  };

  useEffect(() => {
    // Show all side panel as tray if inside demo modal
    setMode(isDemoModal ? 'modal' : 'auto');
  }, [isDemoModal]);

  useEffect(() => {
    // reset demo modal state when unmounting
    return () => {
      setIsDemoModal(false);
      setMode('auto');
    };
  }, []);

  useEffect(() => {
    // Redirect /practice to /practice/main
    const oldPARoute = /\/practice(\?.*)?$/;
    if (oldPARoute.test(window.location.href)) {
      const searchParams = new URLSearchParams(window.location.search);
      const searchObject = Object.fromEntries(searchParams.entries());
      navigate({ replace: true, to: `${URLs.PRACTICE_ANALYTICS_MAIN}`, search: searchObject });
    }
  }, []);

  if (isCheckingPromoteState) {
    return null;
  }

  if (!showPADemoModal || skipTeaserForWeaveUser) {
    return <Outlet />;
  }

  return (
    <FeatureSubscriptionGuard
      feature={Feature.PRACTICE_ANALYTICS}
      demoModalContent={<Outlet />}
      onDemoModalOpen={() => setIsDemoModal(true)}
      onDemoModalClose={handleDemoModalClose}
      allowPromoteState
    >
      <Outlet />
    </FeatureSubscriptionGuard>
  );
};
